import axios from 'axios';

const isLocal =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1');

/** All APIs use dhanwalle.in (proxy on localhost) */
export const TASK_API_BASE = isLocal
  ? '/Api/TaskApi'
  : 'https://dhanwalle.in/Api/TaskApi';

export const LOAN_BACKEND_BASE = isLocal
  ? '/loan_backend'
  : 'https://dhanwalle.in/loan_backend';

export const API_ORIGIN = isLocal ? '' : 'https://dhanwalle.in';

/**
 * Flatten KYC responses.
 * Flat shape (send/verify Aadhaar):
 * { referenceId, responseMessage, responseCode, data? }
 */
export function normalizeKycResponse(payload) {
  const root = payload || {};
  const nested = root.data && typeof root.data === 'object' ? root.data : null;

  // Nested wrapper: { success, data: { responseCode, data } }
  if (
    nested &&
    (nested.responseCode || nested.responseMessage) &&
    nested.data !== undefined
  ) {
    return {
      responseCode: nested.responseCode || root.responseCode || '',
      responseMessage:
        nested.responseMessage ||
        nested.message ||
        root.responseMessage ||
        root.message ||
        '',
      referenceId: nested.referenceId || root.referenceId || '',
      data: nested.data,
      success: root.success,
      status_code: root.status_code,
      raw: root,
    };
  }

  // Flat shape used by dhanwalle loan_backend Aadhaar APIs
  return {
    responseCode: root.responseCode || '',
    responseMessage: root.responseMessage || root.message || root.Message || '',
    referenceId: root.referenceId || '',
    data: root.data !== undefined ? root.data : null,
    success: root.success,
    status_code: root.status_code,
    raw: root,
  };
}

export async function verifyPan(pan) {
  const resp = await axios.post(
    `${LOAN_BACKEND_BASE}/verifyPan.php`,
    { pan },
    {
      headers: { 'Content-Type': 'application/json' },
      validateStatus: () => true,
    }
  );
  return normalizeKycResponse(resp.data);
}

export async function sendAadhaarOtp(aadhaarNumber) {
  const resp = await axios.post(
    `${LOAN_BACKEND_BASE}/sendAadhaarOtp.php`,
    { aadhaar_number: aadhaarNumber },
    {
      headers: { 'Content-Type': 'application/json' },
      validateStatus: () => true,
    }
  );
  return normalizeKycResponse(resp.data);
}

/** Verify OTP — body: aadhaar_number + otp (referenceId optional) */
export async function verifyAadhaarOtp(aadhaarNumber, otp, referenceId) {
  const body = {
    aadhaar_number: aadhaarNumber,
    otp: String(otp),
  };
  if (referenceId) body.referenceId = referenceId;

  const resp = await axios.post(
    `${LOAN_BACKEND_BASE}/verifyAadhaarOtp.php`,
    body,
    {
      headers: { 'Content-Type': 'application/json' },
      validateStatus: () => true,
    }
  );
  return normalizeKycResponse(resp.data);
}

export async function getStates() {
  const resp = await axios.get(`${TASK_API_BASE}/getState`, {
    validateStatus: () => true,
  });
  return resp.data;
}

export async function getCities(stateId) {
  const resp = await axios.post(
    `${TASK_API_BASE}/getCity`,
    { state_id: stateId },
    {
      headers: { 'Content-Type': 'application/json' },
      validateStatus: () => true,
    }
  );
  return resp.data;
}

export async function getPincodesByCityId(cityId) {
  const resp = await axios.post(
    `${TASK_API_BASE}/getPincodeByCityId`,
    { city_id: cityId },
    {
      headers: { 'Content-Type': 'application/json' },
      validateStatus: () => true,
    }
  );
  return resp.data;
}

export async function checkPanBlacklist(pan) {
  const resp = await axios.get(
    `${TASK_API_BASE}/checkGetCustomerbyPanBlacklist`,
    {
      params: { pancard: pan },
      validateStatus: () => true,
    }
  );
  return resp.data || {};
}

export async function checkExistingCustomerByPan(pan) {
  const resp = await axios.get(`${TASK_API_BASE}/checkGetCustomerbyPan`, {
    params: { pancard: pan },
    validateStatus: () => true,
  });
  return resp.data || {};
}

/**
 * Multi-vertical PAN check across loan companies.
 * GET https://dhanwalle.in/checkGetCustomerbyPan/{PAN}
 */
export async function checkMultiVerticalPan(pan) {
  const url = `https://dhanwalle.in/checkGetCustomerbyPan/${encodeURIComponent(pan)}`;

  const resp = await axios.get(url, { validateStatus: () => true });
  return resp.data || {};
}

/** Max DISBURSED verticals allowed before blocking new apply */
export const MAX_DISBURSED_VERTICALS = 2;

/**
 * Count verticals that have at least one DISBURSED record.
 * Returns { allowed, disbursedCount, disbursedVerticals, message }
 *
 * status:false / no data = fresh PAN → allow (other APIs decide eligibility)
 * Only blocks when DISBURSED in 2+ verticals.
 */
export function evaluateMultiVerticalPan(apiData) {
  const verticals = Array.isArray(apiData?.data) ? apiData.data : [];

  // Fresh / not found / empty → do not block here
  if (apiData?.status === false || !verticals.length) {
    return {
      allowed: true,
      disbursedCount: 0,
      disbursedVerticals: [],
      message: '',
    };
  }

  const disbursedVerticals = verticals
    .filter((v) =>
      (v.records || []).some(
        (r) => String(r.lead_status || '').toUpperCase() === 'DISBURSED'
      )
    )
    .map((v) => v.database);

  const disbursedCount = disbursedVerticals.length;

  // Already DISBURSED in 2+ companies → block applying again
  if (disbursedCount >= MAX_DISBURSED_VERTICALS) {
    return {
      allowed: false,
      disbursedCount,
      disbursedVerticals,
      message:
        'You have already taken a loan from another NBFC. A new application cannot be submitted at this time.',
    };
  }

  return {
    allowed: true,
    disbursedCount,
    disbursedVerticals,
    message: '',
  };
}

export async function saveLendingPage(payload) {
  const resp = await axios.post(`${TASK_API_BASE}/saveLendingPage`, payload, {
    headers: { 'Content-Type': 'application/json' },
    validateStatus: () => true,
  });
  return resp.data || {};
}

export function isLeadSaveSuccess(data) {
  return data?.Status === 1 || data?.status === 1 || data?.success === true;
}

export function isPanVerifySuccess(normalized) {
  const code = String(normalized?.responseCode || '').toUpperCase();
  const status = String(
    normalized?.data?.status || normalized?.data?.Status || ''
  ).toUpperCase();
  return code === 'SRC001' && (!status || status === 'VALID');
}

export function isAadhaarSendSuccess(normalized) {
  return String(normalized?.responseCode || '').toUpperCase() === 'SOS174';
}

export function isAadhaarVerifySuccess(normalized) {
  return String(normalized?.responseCode || '').toUpperCase() === 'SRC001';
}

const PAN_CACHE_KEY = 'fundswall_verify_pan_cache';

export function getPanCache(pan) {
  try {
    const raw = localStorage.getItem(PAN_CACHE_KEY);
    if (!raw) return null;
    const map = JSON.parse(raw);
    return map?.[pan] || null;
  } catch {
    return null;
  }
}

export function setPanCache(pan, payload) {
  try {
    const raw = localStorage.getItem(PAN_CACHE_KEY);
    const map = raw ? JSON.parse(raw) : {};
    map[pan] = payload;
    localStorage.setItem(PAN_CACHE_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

export function clearPanCache() {
  try {
    localStorage.removeItem(PAN_CACHE_KEY);
  } catch {
    /* ignore */
  }
}

/** Build address string from Aadhaar verify data fields */
export function buildAadhaarAddress(d = {}) {
  return [
    d.careOf,
    d.house,
    d.street,
    d.locality,
    d.vtcName,
    d.subDistrict,
    d.district,
    d.state,
    d.pincode,
    d.country,
  ]
    .map((x) => String(x || '').trim())
    .filter(Boolean)
    .join(', ');
}
